export type RejectedAction = 'force' | 'edit' | 'discard';

/** The single payload field RejectedItemCard's minimal editor targets for a code. */
export type EditableField = 'plate' | 'qrTag' | 'phone';

/**
 * LOW fix (adversarial review) — `invalid_input` used to map to `plate` as
 * a "most-common-in-practice" heuristic. An audit of every `invalid_input`
 * throw site actually reachable from a replayed outbox item
 * (`create-ticket.use-case.ts` Step 4, `batch-check-in.use-case.ts`) shows
 * it is EXCLUSIVELY the phone E.164 format check — an offline capture's
 * plate is already validated non-empty client-side before enqueue, and the
 * backend's own plate empty-check (`plate.vo.ts`) is never reachable this
 * way. `phone` is now the PRECISE target for this code, not a heuristic.
 *
 * MEDIUM fix (adversarial review) — `ticket_type_disabled` (the ticketType
 * selection itself is disabled at this location — Step 3) and
 * `niche_field_mismatch` (a hotel-only field present at a non-hotel
 * location — Step 6) used to be listed here too, offering an `edit` action
 * whose only actual effect (editing plate) can NEVER fix either failure —
 * the item would just get rejected again with the exact same code,
 * forever. Removed; both now fall back to `[discard]` only, same as any
 * other code this table cannot actually resolve.
 */
const PHONE_EDITABLE_CODES = new Set(['invalid_input']);

/**
 * rejectedActionsFor — maps a rejected item's `lastError.code` to the set of
 * actions RejectedItemCard offers (design §5's error-policy table: "409
 * duplicate_active_ticket → actions [force, discard]. 409 qr_tag_conflict →
 * [edit qrTag, discard]. 422 field-editable (invalid_input) → [edit plate,
 * discard]."). `discard` is ALWAYS available — every rejected item can be
 * given up on. Every code offering `edit` here MUST have a matching entry
 * in `editableFieldFor` — RejectedItemCard only shows the Edit button when
 * both agree an actual field target exists (see that function's doc
 * comment for why `ticket_type_disabled`/`niche_field_mismatch` don't).
 * Unrecognized codes (including the photo-validation 422s — none of which
 * a text-field edit can fix) fall back to `[discard]` only, never invent a
 * force/edit action for a failure mode this table doesn't understand or
 * can't actually resolve.
 */
export function rejectedActionsFor(errorCode: string): RejectedAction[] {
  if (errorCode === 'duplicate_active_ticket') return ['force', 'discard'];
  if (errorCode === 'qr_tag_conflict') return ['edit', 'discard'];
  if (PHONE_EDITABLE_CODES.has(errorCode)) return ['edit', 'discard'];
  return ['discard'];
}

/**
 * editableFieldFor — companion to `rejectedActionsFor`: for a code that
 * offers `edit`, which SINGLE payload field RejectedItemCard's minimal
 * editor should show and patch. `null` for any code that does not offer
 * `edit` (kept as a SEPARATE function, rather than folding into
 * `rejectedActionsFor`'s return shape, so both can be unit tested against
 * the same "which codes are editable" question independently — a
 * mismatch between the two would mean the Edit button renders with no
 * field to show, which RejectedItemCard treats as "do not render Edit"
 * defensively).
 *
 * `qr_tag_conflict` → `'qrTag'` (MEDIUM fix — the item's OWN `qrTag` is
 * the actual conflicting value; editing plate, the card's old ONLY editor,
 * could never resolve it). `invalid_input` → `'phone'` (LOW fix — see
 * `PHONE_EDITABLE_CODES`'s doc comment).
 */
export function editableFieldFor(errorCode: string): EditableField | null {
  if (errorCode === 'qr_tag_conflict') return 'qrTag';
  if (PHONE_EDITABLE_CODES.has(errorCode)) return 'phone';
  return null;
}
